import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, CheckCircle, Users, Target, Sparkles, Brain,
  Rocket, GraduationCap, ClipboardList, Building2, Calendar,
  MapPin, Video, BookOpen, Award, Quote, Mail, Phone, Send,
  Briefcase, Star, Mic, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';
import BrandLogo from '../components/BrandLogo';
import {
  TESTIMONIALS,
  IDEAL_CLIENTS,
  PARTNER_ORGS_FALLBACK as PARTNER_ORGS,
  CASE_STUDIES_FALLBACK as CASE_STUDIES,
  CALENDLY_URL,
} from '../data/workshops';
import { PartnerLogoGrid, CaseStudyCard, normalizeCaseStudy } from '../components/case-studies/CaseStudyCard';
import { PartnerStrip } from '../components/PartnerStrip';
import { MarketingConsentCheckbox } from '../components/MarketingConsentCheckbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  {
    icon: Award,
    title: 'Leadership Development',
    tagline: 'Develop leaders who align teams, drive accountability, and create results.',
    image: '/images/executive-seminar.jpg',
    items: [
      'Executive Leadership Coaching',
      'New Manager Bootcamps',
      'Emerging Leader Programs',
      'Communication & Decision-Making Workshops',
      'Conflict Resolution & Team Dynamics',
      'Leadership Pipeline Strategy',
    ],
  },
  {
    icon: Users,
    title: 'Team Building & Culture Alignment',
    tagline: 'Strengthen connection, communication, and trust inside your organization.',
    image: '/images/group-exercise.jpg',
    items: [
      'Culture Alignment Workshops',
      'Team Communication & Collaboration Training',
      'High-Performing Team Frameworks',
      'Retreats & Offsite Experiences',
      'Team Identity & Values Sessions',
      'Trust & Accountability Workshops',
    ],
  },
  {
    icon: Rocket,
    title: 'Entrepreneurship & Business Growth',
    tagline: 'Help founders, owners, and operators build stronger, more aligned businesses.',
    image: '/images/sbdc-seminar.jpg',
    items: [
      'Business Strategy Workshops',
      'Founder & Operator Coaching',
      'Vision & Strategic Planning',
      'Sales, Marketing & Brand Workshops',
      'Operational Systems & Workflow Training',
      'Funding & Capital Readiness Workshops',
    ],
  },
  {
    icon: Brain,
    title: 'AI & Technology Integration',
    tagline: 'Equip your team with modern tools to operate smarter and faster.',
    image: '/images/top-tech-award.jpg',
    items: [
      'AI for Leaders Workshops',
      'AI Tools for Business Operations',
      'ChatGPT & Workflow Automation Training',
      'AI Strategy for Organizations',
      'Tech Adoption & Change Management',
      'Digital Transformation Roadmapping',
    ],
  },
  {
    icon: GraduationCap,
    title: 'Youth Leadership & Career Development',
    tagline: 'Prepare the next generation of leaders, professionals, and entrepreneurs.',
    image: '/images/leader-in-you.jpg',
    items: [
      'Youth Leadership Bootcamps',
      'College & Career Readiness Programs',
      'Student Entrepreneurship Workshops',
      'School District Trainings',
      'Mentorship & Mindset Coaching',
      'Public Speaking & Confidence Workshops',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Assessments & Diagnostics',
    tagline: 'Measure alignment, leadership effectiveness, and team health with data.',
    image: '/images/disc-assessment.jpg',
    items: [
      'The WE Alignment Index™',
      'DISC Behavioral Assessments',
      'Leadership Style Profiles',
      'Team Dynamics Diagnostics',
      'Organizational Culture Audits',
      'Custom Pre/Post-Engagement Surveys',
    ],
  },
];

const DELIVERY_OPTIONS = [
  { icon: MapPin, label: 'On-Site Workshops', desc: 'In-person training at your location.' },
  { icon: Video, label: 'Virtual Sessions', desc: 'Live, interactive remote training.' },
  { icon: Calendar, label: 'Multi-Day Intensives', desc: 'Deep-dive transformation programs.' },
  { icon: BookOpen, label: 'Custom Curriculum', desc: 'Tailored to your team and goals.' },
  { icon: Building2, label: 'Keynotes & Speaking', desc: 'Energizing talks for events.' },
  { icon: Sparkles, label: 'Hybrid Programs', desc: 'Blended in-person + virtual delivery.' },
];

const SPEAKING_TOPICS = [
  { icon: Target, title: 'The WE Mentality: From "Me" to "We"', desc: 'A keynote on building aligned, accountable, high-trust teams.' },
  { icon: Award, title: 'The 5 Pillars of Organizational Alignment', desc: 'How leaders diagnose and fix breakdowns in communication, role clarity, and execution.' },
  { icon: Brain, title: 'AI for Leaders: Lead Smarter, Not Harder', desc: 'A practical workshop on integrating AI into leadership and operations.' },
  { icon: Users, title: 'Culture by Design, Not Default', desc: 'Architecting team culture intentionally instead of letting it happen by accident.' },
  { icon: Rocket, title: 'The Founder Operating System', desc: 'For entrepreneurs ready to lead with structure, vision, and clarity.' },
  { icon: GraduationCap, title: 'Youth Leadership & Future-Ready Mindsets', desc: 'Building confidence, ownership, and entrepreneurial thinking in students.' },
  { icon: Briefcase, title: 'Workforce Development for the Next Decade', desc: 'Equipping teams for modern work — leadership, AI, and adaptability.' },
  { icon: Mic, title: 'Communication That Builds Trust', desc: 'Practical frameworks for honest, productive, high-impact conversations.' },
];

function SpeakingTopicsCarousel({ onInquire }) {
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    const amount = scrollerRef.current.clientWidth * 0.85;
    scrollerRef.current.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  return (
    <section id="speaking-topics" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-violet-950/15 to-transparent overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Featured Topics</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Speaking & Training Topics
            </h2>
            <p className="text-slate-400 mt-3 max-w-2xl">
              Keynotes, workshops, and intensives Brandon delivers across leadership, AI, workforce, and culture.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scroll('prev')}
              aria-label="Previous topics"
              className="w-11 h-11 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] flex items-center justify-center text-slate-300 hover:text-white transition-all"
              data-testid="speaking-topics-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scroll('next')}
              aria-label="Next topics"
              className="w-11 h-11 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] flex items-center justify-center text-slate-300 hover:text-white transition-all"
              data-testid="speaking-topics-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          data-testid="speaking-topics-carousel"
        >
          {SPEAKING_TOPICS.map((topic, idx) => {
            const Icon = topic.icon;
            return (
              <div
                key={topic.title}
                className="flex-shrink-0 w-[280px] sm:w-[340px] snap-start p-7 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.01] hover:border-cyan-500/40 transition-all duration-300"
                data-testid={`speaking-topic-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20">
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg sm:text-xl font-bold mb-2 leading-tight">{topic.title}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{topic.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={onInquire}
            className="text-cyan-400 hover:text-cyan-300 font-semibold text-base inline-flex items-center gap-2 transition-colors"
            data-testid="speaking-topics-inquire-link"
          >
            Book Brandon for your next event <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}



export default function WorkshopsTraining() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [caseStudies, setCaseStudies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    workshop: 'Leadership Development',
    teamSize: '',
    timeline: '',
    message: '',
  });

  // Fetch case studies from CMS (falls back to static seed data)
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/case-studies`).then(({ data }) => {
      if (!mounted) return;
      const items = data?.items || [];
      if (items.length === 0) return; // keep static fallback below
      // Featured case studies → case study cards (up to 6)
      const featured = items.filter((i) => i.featured).slice(0, 6);
      setCaseStudies(featured);
      // All active → partner logo grid (use logo_url if present)
      setPartners(items);
    }).catch((err) => {
      console.warn('[Workshops] case-studies fetch failed, using fallback:', err?.message);
    });
    return () => { mounted = false; };
  }, []);

  // Pre-fill workshop form from URL query (e.g., from DISC Bird quiz CTAs)
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const service = params.get('service');
    const bird = params.get('bird');
    const source = params.get('source');
    if (!service && !bird && !source) return;

    setForm((prev) => {
      const next = { ...prev };
      if (service) next.workshop = service;
      const contextLines = [];
      if (bird) contextLines.push(`DISC Bird result: ${bird}`);
      if (source) contextLines.push(`Came from: ${source}`);
      if (contextLines.length > 0 && !prev.message.includes('DISC Bird result:')) {
        next.message = `${contextLines.join(' · ')}\n\n${prev.message || ''}`;
      }
      return next;
    });
    setPrefilled(true);

    if (window.location.hash === '#workshop-inquiry') {
      setTimeout(() => {
        document.getElementById('workshop-inquiry')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [prefilled]);

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      const companyMeta = [
        form.company && `Company: ${form.company}`,
        form.phone && `Phone: ${form.phone}`,
        form.workshop && `Interest: ${form.workshop}`,
        form.teamSize && `Team Size: ${form.teamSize}`,
        form.timeline && `Timeline: ${form.timeline}`,
      ].filter(Boolean).join(' | ');

      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: companyMeta || form.company || '—',
        message: `[Workshops & Training Inquiry]\n\n${form.message}`,
        marketing_consent: !!form.marketing_consent,
        source_form: 'workshops_training',
      });
      toast.success('Inquiry sent! Brandon will be in touch within 24 hours.');
      setForm({
        name: '', email: '', phone: '', company: '',
        workshop: 'Leadership Development', teamSize: '', timeline: '', message: '', marketing_consent: false,
      });
    } catch (err) {
      toast.error('Something went wrong. Please try again or email info@thewementality.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToInquiry = () => {
    document.getElementById('workshop-inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* ─────────────────────────────────────── HEADER ─────────────────────────────────────── */}
      <header className="absolute top-0 left-0 right-0 z-30 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 group" data-testid="workshops-logo-link">
            <BrandLogo className="h-10 sm:h-12 w-auto" fallbackClassName="text-base sm:text-lg font-bold tracking-wide text-white whitespace-nowrap" testId="workshops-header-logo-img" />
          </a>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/assessment')}
              variant="outline"
              className="hidden sm:inline-flex border-violet-500/50 text-violet-300 hover:bg-violet-500/10"
              data-testid="workshops-nav-assessment-btn"
            >
              Free Assessment
            </Button>
            <Button
              onClick={scrollToInquiry}
              className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold"
              data-testid="workshops-nav-inquire-btn"
            >
              Inquire Now
            </Button>
          </div>
        </div>
      </header>

      {/* ─────────────────────────────────────── HERO ─────────────────────────────────────── */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-violet-500/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[700px] h-[700px] bg-cyan-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/15 to-cyan-500/15 border border-violet-500/30 mb-6">
                <Sparkles className="h-4 w-4 text-violet-400" />
                <span className="text-violet-300 text-sm font-semibold">Workshops & Training</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6">
                Transformational Training That{' '}
                <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-violet-400 bg-clip-text text-transparent">
                  Aligns Teams
                </span>{' '}
                and Develops Leaders
              </h1>

              <p className="text-lg sm:text-xl text-slate-400 mb-8 leading-relaxed max-w-2xl">
                We build custom workshops, training programs, and leadership intensives that move organizations
                from confusion to clarity, and from disconnection to alignment.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToInquiry}
                  size="lg"
                  className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25"
                  data-testid="workshops-hero-inquire-btn"
                >
                  Request a Custom Workshop <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => window.open(CALENDLY_URL, '_blank')}
                  size="lg"
                  variant="outline"
                  className="border-slate-700 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-xl"
                  data-testid="workshops-hero-book-btn"
                >
                  <Calendar className="mr-2 h-5 w-5" /> Book a Consultation
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400">
                <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400" /> Trusted by 100+ organizations</span>
                <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400" /> Custom-built for your team</span>
                <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-400" /> In-person & virtual</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-violet-500/10">
                <img src="/images/workshop-training.jpg" alt="Workshop training session" className="w-full h-[440px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-xl p-4">
                    <p className="text-xs text-violet-300 font-semibold uppercase tracking-wider mb-1">Featured Program</p>
                    <p className="text-white font-bold text-lg">The Leader In You™ Bootcamp</p>
                    <p className="text-slate-300 text-sm mt-1">A 3-day immersive for emerging executives.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── TRUSTED BY ─────────────────────────────────────── */}
      <PartnerStrip
        heading="Trusted by Leading Organizations"
        subheading="School districts, federal agencies, universities, chambers, and workforce partners across Nevada."
        limit={10}
        showCta={true}
      />

      {/* ─────────────────────────────────────── CATEGORIES ─────────────────────────────────────── */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">What We Train</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Six categories. <span className="text-slate-400 font-medium">Hundreds of outcomes.</span>
            </h2>
            <p className="text-lg text-slate-400">
              Every program is customized to your industry, team size, and strategic goals — no off-the-shelf decks here.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {CATEGORIES.map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.title}
                  className="group relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-violet-500/40 transition-all duration-300"
                  data-testid={`workshop-category-${idx}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={cat.image} alt={cat.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-lg">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">{cat.title}</h3>
                    <p className="text-slate-400 mb-5">{cat.tagline}</p>
                    <ul className="space-y-2.5">
                      {cat.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <CheckCircle className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── DELIVERY OPTIONS ─────────────────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Delivery Options</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              How We Deliver Training
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Choose the format that fits your team — or combine multiple for maximum impact.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DELIVERY_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <div key={opt.label} className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-cyan-500/40 transition-all duration-300">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-violet-500/20 to-cyan-500/20 border border-violet-500/30 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h4 className="text-lg font-bold mb-1.5">{opt.label}</h4>
                  <p className="text-sm text-slate-400">{opt.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── IDEAL CLIENTS ─────────────────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Who We Serve</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              Ideal Clients
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Our workshops are designed for organizations and leaders ready to scale alignment, performance, and culture.
            </p>
            <img src="/images/corporate-boardroom.jpg" alt="Boardroom training" className="rounded-xl border border-white/10 w-full h-72 object-cover hidden lg:block" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {IDEAL_CLIENTS.map((client) => (
              <div key={client} className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-violet-500/40 transition-all">
                <Target className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                <span className="text-slate-200 text-sm font-medium">{client}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────── PAST CLIENTS & RESULTS ─────────────────────────────────── */}
      <section id="past-clients" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-violet-500/8 rounded-full blur-[140px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Past Clients & Results</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Trusted by Organizations{' '}
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">Across Nevada</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              We've partnered with school districts, federal agencies, universities, chambers, municipalities, and nonprofits
              to develop leaders and align teams.
            </p>
          </div>

          {/* Logo Grid — API-backed with static fallback */}
          <div className="mb-20">
            <PartnerLogoGrid
              items={partners.length > 0
                ? partners.map((p) => ({
                    name: p.short_name || p.org_name,
                    sub: p.org_name,
                    logo: p.logo_url || null,
                    website: p.website_url || null,
                  }))
                : PARTNER_ORGS}
            />
          </div>

          {/* Case Study Cards — API-backed with static fallback */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
              <div>
                <p className="text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-2">Engagement Highlights</p>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Case Studies & Outcomes</h3>
              </div>
              <p className="text-sm text-slate-500 max-w-md">
                Real engagements. Honest outcomes. No inflated metrics — just the work and the results it produced.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="case-studies-grid">
              {(caseStudies.length > 0
                ? caseStudies.map(normalizeCaseStudy)
                : CASE_STUDIES.map((cs) => ({ ...cs, id: cs.short }))
              ).map((cs) => (
                <CaseStudyCard key={cs.id || cs.org} cs={cs} />
              ))}
            </div>
          </div>

          {/* Availability Banner */}
          <div className="relative rounded-2xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 via-cyan-500/10 to-violet-500/10 p-6 sm:p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Mic className="h-5 w-5 text-cyan-400" />
              <p className="text-violet-300 text-sm font-bold uppercase tracking-wider">Now Booking</p>
            </div>
            <p className="text-lg sm:text-xl text-white font-semibold">
              Available for Conferences, Retreats, Workforce Programs, and Leadership Events.
            </p>
            <Button
              onClick={scrollToInquiry}
              className="mt-5 bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-6 py-5 rounded-xl"
              data-testid="past-clients-inquire-btn"
            >
              Request Brandon for Your Event <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── FEATURED SPEAKING & TRAINING TOPICS ─────────────────────────────── */}
      <SpeakingTopicsCarousel onInquire={scrollToInquiry} />


      {/* ─────────────────────────────────────── ABOUT BRANDON ─────────────────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-500/10">
              <img src="/images/alignment-workshop.jpg" alt="Brandon Ward leading workshop" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-cyan-300 font-semibold text-sm">Brandon Ward</p>
                <p className="text-white font-bold text-xl">Founder, The WE Mentality</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Your Facilitator</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">
              Led by Brandon Ward
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              Brandon Ward is a leadership consultant, executive coach, and founder of The WE Mentality. He has trained
              executives, government agencies, school districts, and growth-stage companies across the country.
            </p>
            <p className="text-base text-slate-400 leading-relaxed mb-8">
              His approach blends strategic clarity, behavioral science, and real operational experience — helping
              teams move from individual silos to true organizational alignment.
            </p>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">100+</p>
                <p className="text-xs text-slate-400 mt-1">Organizations</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">10K+</p>
                <p className="text-xs text-slate-400 mt-1">Leaders Trained</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">15+</p>
                <p className="text-xs text-slate-400 mt-1">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">What Leaders Say</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Results, In Their Own Words
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="relative p-7 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-violet-500/40 transition-all">
                <Quote className="absolute top-6 right-6 h-8 w-8 text-violet-500/20" />
                <p className="text-slate-200 text-base leading-relaxed mb-6 pr-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  {t.img ? (
                    <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                      {t.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── INQUIRY FORM ─────────────────────────────────────── */}
      <section id="workshop-inquiry" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-violet-950/20 via-slate-950 to-slate-950">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Let's Build Yours</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                Request a Custom Workshop
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Tell us about your team and goals. Brandon will personally review your inquiry and respond within 24 hours.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-semibold">Direct Email</p>
                    <a href="mailto:info@thewementality.com" className="text-slate-400 text-sm hover:text-cyan-400">info@thewementality.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-semibold">Book a Discovery Call</p>
                    <button onClick={() => window.open(CALENDLY_URL, '_blank')} className="text-slate-400 text-sm hover:text-cyan-400 underline-offset-2 hover:underline" data-testid="workshops-discovery-call-link">
                      Schedule on Calendly →
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-semibold">Try The Free Assessment</p>
                    <button onClick={() => navigate('/assessment')} className="text-slate-400 text-sm hover:text-cyan-400 underline-offset-2 hover:underline" data-testid="workshops-assessment-link">
                      Take the WE Alignment Index™ →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                onSubmit={submitInquiry}
                className="p-7 sm:p-9 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm shadow-2xl shadow-violet-500/10"
                data-testid="workshop-inquiry-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="w-name" className="text-slate-300 text-sm">Your Name *</Label>
                    <Input
                      id="w-name"
                      value={form.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="mt-1.5 bg-slate-900/80 border-white/10 text-white"
                      placeholder="Full name"
                      data-testid="workshop-form-name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="w-email" className="text-slate-300 text-sm">Email *</Label>
                    <Input
                      id="w-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="mt-1.5 bg-slate-900/80 border-white/10 text-white"
                      placeholder="you@company.com"
                      data-testid="workshop-form-email"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="w-phone" className="text-slate-300 text-sm">Phone</Label>
                    <Input
                      id="w-phone"
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="mt-1.5 bg-slate-900/80 border-white/10 text-white"
                      placeholder="(555) 555-5555"
                      data-testid="workshop-form-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="w-company" className="text-slate-300 text-sm">Company / Organization</Label>
                    <Input
                      id="w-company"
                      value={form.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      className="mt-1.5 bg-slate-900/80 border-white/10 text-white"
                      placeholder="Your organization"
                      data-testid="workshop-form-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="w-workshop" className="text-slate-300 text-sm">Workshop of Interest</Label>
                    <select
                      id="w-workshop"
                      value={form.workshop}
                      onChange={(e) => handleChange('workshop', e.target.value)}
                      className="mt-1.5 w-full h-10 px-3 rounded-md bg-slate-900/80 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                      data-testid="workshop-form-workshop"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.title} value={c.title}>{c.title}</option>
                      ))}
                      <option value="Custom / Multiple">Custom / Multiple</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="w-team" className="text-slate-300 text-sm">Team Size</Label>
                    <select
                      id="w-team"
                      value={form.teamSize}
                      onChange={(e) => handleChange('teamSize', e.target.value)}
                      className="mt-1.5 w-full h-10 px-3 rounded-md bg-slate-900/80 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                      data-testid="workshop-form-team-size"
                    >
                      <option value="">Select size</option>
                      <option value="1-25">1–25</option>
                      <option value="26-100">26–100</option>
                      <option value="101-500">101–500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="w-timeline" className="text-slate-300 text-sm">Ideal Timeline</Label>
                  <select
                    id="w-timeline"
                    value={form.timeline}
                    onChange={(e) => handleChange('timeline', e.target.value)}
                    className="mt-1.5 w-full h-10 px-3 rounded-md bg-slate-900/80 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                    data-testid="workshop-form-timeline"
                  >
                    <option value="">When would you like to start?</option>
                    <option value="ASAP (within 30 days)">ASAP (within 30 days)</option>
                    <option value="Next quarter">Next quarter</option>
                    <option value="Next 6 months">Next 6 months</option>
                    <option value="Exploring / no timeline">Exploring / no timeline</option>
                  </select>
                </div>

                <div className="mt-4">
                  <Label htmlFor="w-msg" className="text-slate-300 text-sm">Tell us about your goals *</Label>
                  <Textarea
                    id="w-msg"
                    value={form.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    rows={4}
                    className="mt-1.5 bg-slate-900/80 border-white/10 text-white"
                    placeholder="What outcomes are you hoping to achieve? What challenges are you facing right now?"
                    data-testid="workshop-form-message"
                    required
                  />
                </div>

                <div className="mt-5">
                  <MarketingConsentCheckbox
                    checked={form.marketing_consent}
                    onChange={(v) => setForm({ ...form, marketing_consent: v })}
                    note="We'll respond to your inquiry either way."
                    testId="workshop-marketing-consent"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 w-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold py-6 text-base rounded-xl shadow-lg shadow-violet-500/25"
                  data-testid="workshop-form-submit"
                >
                  {submitting ? 'Sending...' : (
                    <>Send Inquiry <Send className="ml-2 h-4 w-4" /></>
                  )}
                </Button>

                <p className="mt-4 text-xs text-slate-500 text-center">
                  We typically respond within 24 hours. Your information stays private.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── FOOTER ─────────────────────────────────────── */}
      <footer className="border-t border-white/10 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-9 w-auto" fallbackClassName="text-sm font-bold tracking-wide text-white whitespace-nowrap" testId="workshops-footer-logo-img" />
            <span className="text-slate-400 text-sm">© {new Date().getFullYear()} {companyInfo.name}</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="/" className="text-slate-400 hover:text-white transition-colors">Home</a>
            <a href="/assessment" className="text-slate-400 hover:text-white transition-colors">Assessment</a>
            <a href="mailto:info@thewementality.com" className="text-slate-400 hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
